import React, { useState } from "react";
import { supabase } from "../../../supabaseClient";
import { useAdmin } from "../../../context/AdminContext";
import { useSeason } from "../../../context/SeasonContext";
import { InlineNotice } from "../../../utils/InlineNotice";
import { useInlineNotice } from "../../../hooks/useInlineNotice";

/**
 * AddTeamModal
 *
 * Props:
 *   onClose  — () => void
 *   onCreated — () => void  called after successful creation
 *
 * NOTE: New teams are always created against the open REGISTRATION season
 * (not whichever season the dashboard happens to be viewing). During overlap
 * periods — e.g. Season 3 playoffs running alongside Season 4 registration —
 * a manually-added team should land where captains are signing up, not
 * wherever the admin's season switcher is currently pointed.
 */
const AddTeamModal = ({ onClose, onCreated }) => {
  const { adminId, loadData } = useAdmin();
  const { registrationSeason } = useSeason();

  const [form, setForm] = useState({
    teamName: "",
    captainName: "",
    captainEmail: "",
  });
  const [creating, setCreating] = useState(false);
  const { notice, showError, clear } = useInlineNotice();

  const handleCreate = async () => {
    clear();
    if (!form.teamName.trim()) {
      showError("Team name is required");
      return;
    }
    if (!registrationSeason) {
      showError("No open registration season found — check Season Management.");
      return;
    }

    setCreating(true);
    try {
      const { error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "createTeam",
          adminId,
          payload: {
            seasonId: registrationSeason.id,
            teamName: form.teamName.trim(),
            captainName: form.captainName.trim() || null,
            captainEmail: form.captainEmail.trim() || null,
          },
        },
      });
      if (error) throw error;
      await loadData();
      onCreated?.();
      onClose();
    } catch (err) {
      showError(`Error creating team: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-md w-full space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Add New Team
        </h2>

        {registrationSeason && (
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
            Adding to{" "}
            <span className="font-semibold">
              {registrationSeason.name || registrationSeason.label}
            </span>{" "}
            (open registration)
          </p>
        )}

        <InlineNotice notice={notice} onDismiss={clear} />

        <div className="space-y-4">
          {[
            {
              label: "Team Name",
              key: "teamName",
              type: "text",
              placeholder: "e.g. Al-Mu'minoon Warriors",
              required: true,
            },
            {
              label: "Captain Name",
              key: "captainName",
              type: "text",
              placeholder: "Captain Full Name",
              required: false,
            },
            {
              label: "Captain Email",
              key: "captainEmail",
              type: "email",
              placeholder: "captain@example.com",
              required: false,
            },
          ].map(({ label, key, type, placeholder, required }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
                {!required && (
                  <span className="text-gray-400 font-normal"> (optional)</span>
                )}
              </label>
              <input
                type={type}
                value={form[key]}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, [key]: e.target.value }))
                }
                placeholder={placeholder}
                autoFocus={key === "teamName"}
                className="w-full border dark:border-gray-600 p-3 rounded-lg
                                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                           placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-8">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-300 dark:bg-gray-600 dark:text-white rounded-lg font-medium hover:bg-gray-400 dark:hover:bg-gray-500 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !form.teamName.trim()}
            className="flex-1 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400
                                   text-white rounded-lg font-semibold transition"
          >
            {creating ? "Creating…" : "Create Team"}
          </button>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          You can add players and edit details after creation.
        </p>
      </div>
    </div>
  );
};

export default AddTeamModal;
